import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, ExternalLink } from 'lucide-react';
import { SEO } from '../../../components/SEO';

const UPP_AMBER = '#D97706';

/* ─── Shared sub-components ────────────────────────────────────── */
const UpsellTopBar = () => (
  <div
    className="sticky top-0 z-50 border-b border-[#E2E8F0]"
    style={{ background: 'rgba(248,247,244,0.95)', backdropFilter: 'blur(20px)' }}
  >
    <div className="container-custom py-4 flex items-center justify-between">
      <Link
        to="/apps/upsell-plans-pro"
        className="flex items-center gap-2 text-[#374151] hover:text-[#0F172A] text-sm font-medium transition-colors group"
      >
        <ArrowLeft size={15} className="group-hover:-translate-x-0.5 transition-transform duration-200" />
        Back to Upsell Plans Pro
      </Link>
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 flex items-center justify-center rounded-sm" style={{ backgroundColor: UPP_AMBER }}>
          <ShieldCheck size={15} color="#fff" />
        </div>
        <span className="font-display text-[1.2rem] font-bold text-[#0F172A]">Upsell Plans Pro</span>
      </div>
      <a
        href="#"
        className="hidden sm:inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white transition-opacity hover:opacity-90"
        style={{ backgroundColor: '#95BF47' }}
      >
        Add to Shopify
        <ExternalLink size={12} />
      </a>
    </div>
  </div>
);

const UpsellFooter = () => (
  <div className="border-t border-[#E2E8F0] py-6">
    <div className="container-custom flex flex-col sm:flex-row items-center justify-between gap-3">
      <p className="font-mono text-[0.55rem] tracking-widest text-[#9CA3AF] uppercase">
        © {new Date().getFullYear()} Upsell Plans Pro · A Smart Solutions Product
      </p>
      <div className="flex items-center gap-4">
        <Link to="/apps/upsell-plans-pro/terms" className="font-mono text-[0.55rem] tracking-widest uppercase" style={{ color: UPP_AMBER }}>Terms</Link>
        <span className="text-[#E2E8F0]">·</span>
        <Link to="/apps/upsell-plans-pro/privacy" className="font-mono text-[0.55rem] tracking-widest text-[#9CA3AF] hover:text-[#374151] uppercase transition-colors">Privacy</Link>
      </div>
    </div>
  </div>
);

const Section = ({ num, title, children }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5 }}
  >
    <div className="flex items-start gap-4 mt-12 mb-3">
      <span className="font-mono text-[0.6rem] tracking-widest uppercase shrink-0 mt-1.5" style={{ color: UPP_AMBER }}>{num}</span>
      <h2 className="font-display text-xl font-bold text-[#0F172A]">{title}</h2>
    </div>
    <div className="pl-8">{children}</div>
  </motion.div>
);

const P = ({ children }) => (
  <p className="text-[#374151] text-sm leading-relaxed mb-3">{children}</p>
);

/* ─── Page ──────────────────────────────────────────────────────── */
export const UpsellPlansProTermsPage = () => {
  return (
    <div className="min-h-screen bg-[#F8F7F4] flex flex-col">
      <SEO
        title="Upsell Plans Pro Terms & Conditions"
        description="Terms and conditions for the Upsell Plans Pro Shopify app by Smart Solutions."
        path="/apps/upsell-plans-pro/terms"
      />
      <UpsellTopBar />

      <div className="flex-1">
        <div className="container-custom py-16 max-w-3xl mx-auto">

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="section-label">Legal</span>
              <span className="w-6 h-px bg-[#2563EB]/30" />
            </div>
            <h1
              className="font-display font-bold text-[#0F172A] mb-3"
              style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}
            >
              Terms &amp; Conditions
            </h1>
            <p className="font-mono text-[0.65rem] tracking-widest text-[#9CA3AF] uppercase mb-8">
              Last updated: October 14, 2026
            </p>
            <div className="divider-gold opacity-40" />
          </motion.div>

          {/* Intro */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mt-8"
          >
            <P>
              Please read these Terms and Conditions carefully before installing or using Upsell Plans Pro. By
              installing the app from the Shopify App Store, you confirm that you have read, understood, and agree to
              be bound by these Terms. These Terms form a legal agreement between you (the Merchant) and Smart
              Solutions, the operator of Upsell Plans Pro.
            </P>
          </motion.div>

          <Section num="01" title="Acceptance of Terms">
            <P>
              By installing or using Upsell Plans Pro ("the App"), you ("Merchant") agree to these Terms and
              Conditions. If you do not agree, you must not install or use the App. These Terms apply to all users of
              the App, including merchant account holders and any staff members given access.
            </P>
          </Section>

          <Section num="02" title="Description of Service">
            <P>
              Upsell Plans Pro is a Shopify app that lets merchants sell protection plans (extended warranties), paid
              services such as installation, assembly, and setup, and product add-ons on their storefront. The App
              registers every plan sold, provides customers with a self-serve page to view coverage and file claims,
              and gives merchants tools to manage claims, installations, analytics, and automations.
            </P>
            <P>
              The App is delivered as a Software-as-a-Service (SaaS) product installed via the Shopify App Store and
              hosted on Microsoft Azure cloud infrastructure.
            </P>
          </Section>

          <Section num="03" title="You Are the Plan Provider — Not an Insurer">
            <P>
              The App is software only. Smart Solutions is not an insurer, warrantor, or service provider, and is not
              a party to any protection plan, service, or add-on you sell to your customers.
            </P>
            <P>
              You, the merchant, are the sole provider of every protection plan and service sold through the App. You
              set the coverage terms, prices, and claim limits; you decide and pay all claims; and you deliver any
              installation or other services. You are responsible for honoring every plan and service you sell for
              its full term, even if you later stop using the App.
            </P>
            <P>
              You are responsible for making sure the plans and services you offer comply with all consumer
              protection, warranty, insurance, tax, and other laws that apply where you sell, including any licensing
              or disclosure requirements.
            </P>
          </Section>

          <Section num="04" title="Merchant Responsibilities">
            <P>
              You are responsible for the accuracy of the offers, coverage terms, and pricing you configure in the
              App, and for the information shown to customers on your storefront and protection page. You are
              responsible for responding to customer claims and messages, managing your installers, and keeping your
              installer directory accurate.
            </P>
            <P>
              You must ensure that your use of the App, including the personal data of your customers and installers
              that you collect through it, complies with applicable laws and Shopify's policies.
            </P>
          </Section>

          <Section num="05" title="Acceptable Use Policy">
            <P>
              You agree not to use the App for any unlawful, harmful, or abusive purpose. You must not use the App to
              sell plans or services for unlawful products, or to make misleading claims about coverage.
            </P>
            <P>
              You must not misuse the customer protection page or the photo upload feature, including uploading
              content that is illegal, defamatory, or that violates any third-party rights. You must not attempt to
              reverse-engineer, decompile, or otherwise extract the source code of the App, or use the App in a way
              that could damage, disable, overburden, or impair our infrastructure.
            </P>
          </Section>

          <Section num="06" title="Plans, Billing & Refunds">
            <P>
              The App offers a free plan and paid plans as listed on the Shopify App Store. All charges are billed
              through Shopify and appear on your Shopify invoice. Paid plans include a 14-day free trial. We do not
              take a commission on the plans, services, or add-ons you sell.
            </P>
            <P>
              Each plan includes a monthly upsell revenue limit and a number of live offers. Revenue limits reset on
              the 1st of each month. If you exceed your plan's limit, the App continues to work; if you remain over
              the limit for 14 days, a small "Powered by Upsell" badge is shown on your storefront widget until you
              upgrade or the limit resets.
            </P>
            <P>
              Fees are non-refundable, and we do not provide refunds or credits for partial billing periods,
              downgrades, or unused time. You may cancel at any time by uninstalling the App or changing your plan.
            </P>
          </Section>

          <Section num="07" title="Intellectual Property">
            <P>
              All intellectual property rights in the App — including but not limited to the software, user
              interface, design, and documentation — are owned by Smart Solutions or its licensors. These Terms do
              not grant you any rights in or to the App beyond the limited right to use it in accordance with these
              Terms.
            </P>
            <P>
              You retain all ownership rights in the data you enter into the App, including your offers, coverage
              terms, and customer records.
            </P>
          </Section>

          <Section num="08" title="Data and Privacy">
            <P>
              The App collects and processes certain merchant and customer data in order to provide its
              functionality. Full details of what data we collect, how we use it, and your rights are described in
              our{' '}
              <Link to="/apps/upsell-plans-pro/privacy" style={{ color: UPP_AMBER }} className="hover:underline">
                Privacy Policy
              </Link>
              , which forms part of these Terms.
            </P>
            <P>
              By using the App, you acknowledge that you have read and agree to our Privacy Policy.
            </P>
          </Section>

          <Section num="09" title="Limitation of Liability">
            <P>
              To the fullest extent permitted by applicable law, Smart Solutions and its affiliates, directors, and
              employees shall not be liable for any indirect, incidental, special, consequential, or punitive damages
              arising out of or in connection with your use of the App, including but not limited to loss of data,
              loss of revenue, or business interruption. Smart Solutions has no liability for any claim, warranty
              obligation, or service commitment arising from the plans and services you sell.
            </P>
            <P>
              Our total aggregate liability to you under these Terms shall not exceed the amount paid by you for the
              App in the three months preceding the event giving rise to the claim.
            </P>
          </Section>

          <Section num="10" title="Indemnification">
            <P>
              You agree to indemnify, defend, and hold harmless Smart Solutions and its affiliates from and against
              any claims, liabilities, damages, losses, and expenses (including reasonable legal fees) arising out of
              or in connection with your use of the App, the protection plans and services you sell, any claim made by
              your customers, your breach of these Terms, or your violation of any applicable law or third-party
              right.
            </P>
          </Section>

          <Section num="11" title="Modifications to the Service or Terms">
            <P>
              We reserve the right to update, modify, or discontinue the App or any part of it at any time, with or
              without notice. We may also update these Terms or the App's pricing from time to time. If we make
              material changes, we will notify you via the Shopify admin or by email.
            </P>
            <P>
              Continued use of the App after changes to these Terms take effect constitutes your acceptance of the
              revised Terms.
            </P>
          </Section>

          <Section num="12" title="Termination">
            <P>
              You may terminate your use of the App at any time by uninstalling it from your Shopify store. Upon
              uninstallation, access to the App and its storefront widget will cease immediately. Uninstalling the
              App does not end your obligations to customers under plans and services you have already sold.
            </P>
            <P>
              <strong className="font-medium text-[#0F172A]">Data retention:</strong> Sessions are deleted
              immediately on uninstall, and all of your store's data is permanently deleted when Shopify sends the
              shop redaction request, approximately 48 hours after uninstall. Export any plan or claim records you
              need before uninstalling.
            </P>
            <P>
              We reserve the right to suspend or terminate your access to the App at any time if we reasonably
              believe you have breached these Terms.
            </P>
          </Section>

          <Section num="13" title="Contact Information">
            <P>
              If you have any questions about these Terms, please contact us:
            </P>
            <div className="bg-white border border-[#E2E8F0] p-5 mt-2" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <p className="text-sm font-medium text-[#0F172A]">Smart Solutions</p>
              <p className="text-sm text-[#374151] mt-1">
                <a href="mailto:hello@smartsolution.ai" style={{ color: UPP_AMBER }} className="hover:underline">
                  hello@smartsolution.ai
                </a>
              </p>
              <p className="text-sm text-[#374151]">Kuwait City, Kuwait</p>
            </div>
          </Section>

          <div className="divider-gold opacity-30 mt-16" />
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mt-6">
            <p className="font-mono text-[0.6rem] tracking-widest text-[#9CA3AF] uppercase">
              Last updated: October 14, 2026
            </p>
            <Link
              to="/apps/upsell-plans-pro/privacy"
              className="font-mono text-[0.6rem] tracking-widest hover:underline uppercase"
              style={{ color: UPP_AMBER }}
            >
              View Privacy Policy →
            </Link>
          </div>
        </div>
      </div>

      <UpsellFooter />
    </div>
  );
};
